import {
  CHROME_BRAND_ID,
  CHROME_HEADER_ID,
  CHROME_MINIMIZE_ID,
  CHROME_TEAM_ID,
  MINIMIZE_LABEL_RE,
  PRIMARY_NAV_SEL,
  SECONDARY_NAV_SEL,
  SEND_BTN_CLASS,
  STYLE_ID,
  TEAM_AVATARS,
  UPLOAD_LABEL_RE,
} from './constants';
import { buttonLabel, revealEl } from './dom';
import { closeTawkChat } from './visibility';

const composerDocs = new WeakSet();

const MINIMIZE_SVG =
  '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">' +
  '<path fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" d="M5 12h14"/>' +
  '</svg>';

const SEND_SVG =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M3.4 20.6 21 12 3.4 3.4v6.35L14.2 12 3.4 14.25Z"/></svg>';

const BRAND_SVG =
  '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" aria-hidden="true">' +
  '<path d="M8 2L3 4v4c0 2.76 2.15 5.34 5 6 2.85-.66 5-3.24 5-6V4L8 2z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>' +
  '</svg>';

const FILE_UPLOAD_SEL =
  'input[type="file"], .tawk-chatinput-button, .tawk-chatinput-fileupload, .tawk-chatinput-fileupload-input';

function submitTawkEditor(textarea) {
  if (!textarea) return;
  try {
    textarea.focus();
  } catch {
    // ignore
  }
  const eventInit = { key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true, cancelable: true };
  textarea.dispatchEvent(new KeyboardEvent('keydown', eventInit));
  textarea.dispatchEvent(new KeyboardEvent('keypress', eventInit));
  textarea.dispatchEvent(new KeyboardEvent('keyup', eventInit));
}

function isUploadControl(el) {
  return (
    el.matches?.('input[type="file"], .tawk-chatinput-fileupload, .tawk-chatinput-fileupload-input') ||
    UPLOAD_LABEL_RE.test(buttonLabel(el))
  );
}

function bindComposerGuards(doc) {
  if (doc.documentElement.dataset.richuelSend) return;
  doc.documentElement.dataset.richuelSend = '1';

  doc.addEventListener(
    'click',
    (event) => {
      const upload = event.target.closest?.(FILE_UPLOAD_SEL);
      if (upload && isUploadControl(upload)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const btn = event.target.closest?.(`.${SEND_BTN_CLASS}`);
      if (!btn) return;
      event.preventDefault();
      const wrap = btn.closest('.tawk-chatinput-wrap');
      const nativeSend = wrap?.querySelector('.tawk-chatinput-send');
      if (nativeSend) nativeSend.click();
      else submitTawkEditor(wrap?.querySelector('.tawk-chatinput-editor'));
    },
    true
  );

  const blockFiles = (event) => {
    if (event.dataTransfer?.files?.length) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  doc.addEventListener('dragover', blockFiles, true);
  doc.addEventListener('drop', blockFiles, true);
  doc.addEventListener(
    'paste',
    (event) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.kind === 'file') {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }
    },
    true
  );
}

function disableTawkFileUpload(doc) {
  if (!doc?.querySelectorAll) return;
  doc.querySelectorAll('input[type="file"]').forEach((input) => {
    input.disabled = true;
    input.tabIndex = -1;
    input.value = '';
    input.style.setProperty('display', 'none', 'important');
  });
  doc.querySelectorAll('.tawk-chatinput-button, .tawk-chatinput-fileupload, .tawk-chatinput-fileupload-input').forEach((el) => {
    if (el.matches?.('.tawk-chatinput-button') && !UPLOAD_LABEL_RE.test(buttonLabel(el))) return;
    el.style.setProperty('display', 'none', 'important');
    el.setAttribute('aria-hidden', 'true');
  });
}

function ensureSendButton(doc) {
  doc.querySelectorAll('.tawk-chatinput-wrap').forEach((wrap) => {
    if (wrap.querySelector(`.${SEND_BTN_CLASS}`)) return;
    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.className = SEND_BTN_CLASS;
    btn.setAttribute('aria-label', 'Send message');
    btn.innerHTML = SEND_SVG;
    wrap.appendChild(btn);
  });
}

function enhanceTawkComposer(doc) {
  if (!doc || !doc.querySelectorAll) return;
  const style = doc.getElementById(STYLE_ID);
  if (style && style.parentNode && style.nextSibling) style.parentNode.appendChild(style);
  bindComposerGuards(doc);
  ensureSendButton(doc);
  disableTawkFileUpload(doc);
}

function revealNavButtons(root, skip) {
  root.querySelectorAll('button, [role="button"]').forEach((btn) => {
    if (skip?.(btn)) return;
    revealEl(btn);
  });
}

function showTawkPrimaryNav(doc) {
  if (!doc?.querySelectorAll) return;
  doc.querySelectorAll(PRIMARY_NAV_SEL).forEach((el) => {
    revealEl(el);
    revealNavButtons(el);
  });
}

function showTawkSecondaryNav(doc) {
  if (!doc?.querySelectorAll) return;
  doc.querySelectorAll(SECONDARY_NAV_SEL).forEach((el) => {
    revealEl(el);
    revealNavButtons(
      el,
      (btn) => btn.id === CHROME_MINIMIZE_ID || MINIMIZE_LABEL_RE.test(buttonLabel(btn))
    );
  });
}

function hideNativeMinimize(toolbar) {
  if (!toolbar?.querySelectorAll) return;
  toolbar.querySelectorAll('button, [role="button"]').forEach((btn) => {
    if (btn.id === CHROME_MINIMIZE_ID) return;
    if (!MINIMIZE_LABEL_RE.test(buttonLabel(btn))) return;
    btn.style.setProperty('display', 'none', 'important');
    btn.setAttribute('aria-hidden', 'true');
  });
}

function placePrimaryBesideSecondary(toolbar) {
  if (!toolbar) return;
  const primary = toolbar.querySelector(
    '[role="navigation"][aria-label="primary"], .tawk-toolbar-nav[aria-label="primary"]'
  );
  const secondary = toolbar.querySelector(
    '[role="navigation"][aria-label="secondary"], .tawk-toolbar-nav[aria-label="secondary"]'
  );
  const header = toolbar.querySelector(`#${CHROME_HEADER_ID}`);
  const minBtn = toolbar.ownerDocument?.getElementById?.(CHROME_MINIMIZE_ID);

  if (header && minBtn) {
    if (minBtn.nextElementSibling !== header || minBtn.parentElement !== toolbar) {
      header.insertAdjacentElement('beforebegin', minBtn);
    }
  } else if (header && toolbar.firstElementChild !== header) {
    toolbar.insertBefore(header, toolbar.firstChild);
  }

  if (primary && secondary && primary.nextElementSibling !== secondary) {
    secondary.insertAdjacentElement('beforebegin', primary);
  } else if (primary && !secondary && primary.parentElement === toolbar) {
    toolbar.appendChild(primary);
  }
}

function ensureMinimizeButton(doc, toolbar) {
  if (!toolbar) return;
  const header = toolbar.querySelector(`#${CHROME_HEADER_ID}`);
  let btn = doc.getElementById(CHROME_MINIMIZE_ID);
  if (!btn) {
    btn = doc.createElement('button');
    btn.id = CHROME_MINIMIZE_ID;
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Hide chat');
    btn.innerHTML = MINIMIZE_SVG;
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeTawkChat();
    });
  }
  if (header) {
    if (btn.nextElementSibling !== header || btn.parentElement !== toolbar) {
      header.insertAdjacentElement('beforebegin', btn);
    }
  } else if (toolbar.firstElementChild !== btn) {
    toolbar.insertBefore(btn, toolbar.firstChild);
  }
}

function ensureHeader(doc, toolbar) {
  if (!toolbar || doc.getElementById(CHROME_HEADER_ID)) return;
  const header = doc.createElement('div');
  header.id = CHROME_HEADER_ID;
  header.innerHTML =
    '<p class="richuel-tawk-title">Chat with our team</p>' +
    '<p class="richuel-tawk-sub">We will respond within 24 hours</p>';
  toolbar.insertBefore(header, toolbar.firstChild);
}

function ensureTeamRow(doc, toolbar) {
  if (!toolbar || doc.getElementById(CHROME_TEAM_ID)) return;
  const team = doc.createElement('div');
  team.id = CHROME_TEAM_ID;
  const avatars = TEAM_AVATARS.map(
    (src) => `<img src="${src}" alt="" width="36" height="36" draggable="false" />`
  ).join('');
  team.innerHTML =
    `<div class="richuel-tawk-avatars">${avatars}</div>` +
    '<p class="richuel-tawk-online">We&rsquo;re online and ready to help.</p>';
  toolbar.after(team);
}

function ensureBrand(doc) {
  if (doc.getElementById(CHROME_BRAND_ID)) return;
  const footer = doc.querySelector('.tawk-footer') || doc.body;
  const brand = doc.createElement('div');
  brand.id = CHROME_BRAND_ID;
  brand.innerHTML = `${BRAND_SVG}<span>Powered by Richuel</span>`;
  footer.appendChild(brand);
}

export function enhanceTawkPanel(doc) {
  if (!doc?.body) return;
  const toolbar = doc.querySelector('.tawk-toolbar');
  showTawkPrimaryNav(doc);
  showTawkSecondaryNav(doc);
  hideNativeMinimize(toolbar);
  ensureHeader(doc, toolbar);
  ensureMinimizeButton(doc, toolbar);
  placePrimaryBesideSecondary(toolbar);
  ensureTeamRow(doc, toolbar);
  ensureBrand(doc);
}

export function watchTawkComposer(doc) {
  if (!doc || composerDocs.has(doc)) return;
  composerDocs.add(doc);
  enhanceTawkComposer(doc);
  enhanceTawkPanel(doc);
  try {
    const observer = new MutationObserver(() => {
      enhanceTawkComposer(doc);
      enhanceTawkPanel(doc);
    });
    observer.observe(doc.documentElement || doc.body, { childList: true, subtree: true });
  } catch {
    // ignore
  }
}
